
import { useState, useEffect } from 'react';
import { useToast } from '@/hooks/use-toast';
import { getAuthHeader, isAdmin as checkIsAdmin } from '@/services/authService';

export interface AnalyticsData {
  totalPosts: number;
  publishedPosts: number;
  draftPosts: number;
  totalViews: number;
  totalClicks: number;
  ctr: number;
  topPosts: {
    id: string;
    title: string;
    slug: string;
    view_count: number;
  }[];
  monthlyData: {
    month: string;
    clicks: number;
  }[];
  message?: string; // Optional message from the server about data quality
  error_details?: string; // Optional error details if using fallback data
}

export function useAnalytics() {
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const isAdmin = await checkIsAdmin();
        if (!isAdmin) {
          throw new Error("You must be an admin to view analytics");
        }
        
        console.log("Calling analytics function");
        
        // Call the edge function (no need for service role key in the frontend)
        const response = await fetch('/api/get-analytics', {
          headers: {
            ...getAuthHeader(),
          },
        });

        if (!response.ok) {
          throw new Error(`Error fetching analytics: ${response.statusText}`);
        }

        const analyticsData: AnalyticsData = await response.json();
        
        // Validate and provide safe default values for all properties
        const safeData: AnalyticsData = {
          totalPosts: analyticsData.totalPosts || 0,
          publishedPosts: analyticsData.publishedPosts || 0,
          draftPosts: analyticsData.draftPosts || 0,
          totalViews: analyticsData.totalViews || 0,
          totalClicks: analyticsData.totalClicks || 0,
          ctr: analyticsData.ctr || 0,
          topPosts: analyticsData.topPosts || [],
          monthlyData: analyticsData.monthlyData || [],
          message: analyticsData.message,
          error_details: analyticsData.error_details
        };
        
        setData(safeData);
      } catch (err: any) {
        console.error('Error fetching analytics:', err);
        setError(err instanceof Error ? err : new Error(err.message || 'Unknown error occurred'));
        
        toast({
          title: "Error loading analytics",
          description: err.message || "Could not load analytics data. Please try logging out and back in.",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchAnalytics();
  }, [toast]);

  return { data, isLoading, error };
}
